import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw, Sparkles, Square } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import { generaVerbale } from '../../services/verbaliAudioService';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { AVVISO_GENERAZIONE_PUBBLICATO } from './VerbaleAvvio';
import { useToast } from '../../contexts/ToastContext';
import {
  TESTO_GENERA_INTERROTTO, TESTO_GENERA_SOSTITUISCE, TESTO_GENERAZIONE_GIA_IN_CORSO, TESTO_GENERAZIONE_SUL_SERVER,
} from './testiAudio';

/** `fine` della generazione: il verbale salvato dal server. */
export interface FineGenerazione {
  contenuto: string;
  riferimenti?: unknown[];
  ultimaModifica?: string | null;
}

interface VerbaleGeneraProps {
  eventId: string;
  /** Il foglio ha già del testo: prima si chiede conferma. */
  haContenuto: boolean;
  onFine: (dati: FineGenerazione) => void;
  /** Prima di generare (es. salva le modifiche in sospeso): false ⇒ non si genera. */
  primaDiGenerare?: () => Promise<boolean>;
  /** Generazione in corso in questa finestra (la pagina blocca il foglio). */
  onInCorso?: (inCorso: boolean) => void;
  /**
   * Il server continua senza la finestra (interrotta o rete caduta a stream aperto, oppure già in
   * corso): la pagina aspetta la fine rileggendo il verbale. Con questa prop l'interruzione lo dice.
   */
  onSulServer?: () => void;
  /** Un'impaginazione o generazione è già in corso sul server. */
  disabilitato?: boolean;
  /** Verbale già pubblicato: il testo generato lo vedono subito i lettori. */
  pubblicato?: boolean;
}

const ERRORE_SALVATAGGIO = 'Non sono riuscito a salvare il verbale attuale: riprova.';

interface SezioneAnteprima {
  chiave: string;
  titolo: string;
  /** Testo in arrivo (grezzo, mostrato senza tag). */
  testo: string;
  /** HTML finale della sezione (`sezioneFine`). */
  html: string | null;
}

type Fase = 'pronta' | 'in_corso' | 'fatta' | 'interrotta' | 'sul_server' | 'gia_in_corso' | 'errore';

/** Marcatore di tempo `⟦A1 00:10:05⟧`, anche ancora senza chiusura mentre arriva (come il backend). */
const MARCATORE = /⟦\s*(?:A\d{0,3})?\s*[\d:]{0,8}⟧?/g;

/** Testo in arrivo leggibile: senza tag né marcatori di tempo, le ultime righe. */
export const testoInArrivo = (grezzo: string) =>
  grezzo
    .replace(MARCATORE, '')
    .replace(/<[^>]*>?/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(-500);

/** «Genera verbale dalla trascrizione»: anteprima per sezione in streaming, «Interrompi», nulla salvato se fallisce. */
const VerbaleGenera: React.FC<VerbaleGeneraProps> = ({
  eventId, haContenuto, onFine, primaDiGenerare, onInCorso, onSulServer, disabilitato = false, pubblicato = false,
}) => {
  const toast = useToast();
  const [fase, setFase] = useState<Fase>('pronta');
  const [conferma, setConferma] = useState(false);
  const [sezioni, setSezioni] = useState<SezioneAnteprima[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const anteprimaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  // Il lavoro sul server è finito (la pagina toglie il blocco): l'avviso «continua sul server» si chiude.
  const disabilitatoPrimaRef = useRef(disabilitato);
  useEffect(() => {
    const prima = disabilitatoPrimaRef.current;
    disabilitatoPrimaRef.current = disabilitato;
    if (prima && !disabilitato && (fase === 'sul_server' || fase === 'gia_in_corso')) {
      setFase('pronta');
      // Le sezioni parziali dello stream interrotto non valgono più: il foglio ha il testo del server.
      setSezioni([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabilitato]);

  // L'anteprima segue il testo che arriva.
  useEffect(() => {
    const el = anteprimaRef.current;
    if (el && fase === 'in_corso') el.scrollTop = el.scrollHeight;
  }, [sezioni, fase]);

  const avvia = async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setFase('in_corso');
    setErrore(null);
    setSezioni([]);
    onInCorso?.(true);
    if (primaDiGenerare) {
      let salvato = false;
      try { salvato = await primaDiGenerare(); } catch { salvato = false; }
      if (!salvato) {
        if (controllerRef.current === controller) controllerRef.current = null;
        setErrore(ERRORE_SALVATAGGIO);
        setFase('errore');
        onInCorso?.(false);
        return;
      }
    }
    let finito = false;
    let fallito = false;
    let aperto = false;
    let persa = false;
    let giaInCorso = false;
    await generaVerbale(eventId, {}, {
      aperto: () => { aperto = true; },
      connessionePersa: () => { persa = true; },
      rifiutato: (stato, corpo) => {
        if (stato === 409 && (corpo?.codice === 'IMPAGINAZIONE_IN_CORSO' || corpo?.code === 'IMPAGINAZIONE_IN_CORSO')) giaInCorso = true;
      },
      evento: (nome, dati) => {
        if (controller.signal.aborted) return;
        if (nome === 'sezione' && dati?.chiave) {
          setSezioni((s) => [...s, { chiave: String(dati.chiave), titolo: String(dati.titolo || dati.chiave), testo: '', html: null }]);
        } else if (nome === 'sezioneFine' && dati?.chiave) {
          setSezioni((s) => s.map((x) => (x.chiave === dati.chiave ? { ...x, html: typeof dati.html === 'string' ? dati.html : '' } : x)));
        }
      },
      delta: (testo) => {
        if (controller.signal.aborted) return;
        setSezioni((s) => {
          if (!s.length) return s;
          const ultima = s[s.length - 1];
          return [...s.slice(0, -1), { ...ultima, testo: ultima.testo + testo }];
        });
      },
      fine: (dati) => {
        if (controller.signal.aborted) return;
        finito = true;
        setFase('fatta');
        toast.success('Verbale generato dalla trascrizione', 'Rileggilo sezione per sezione.');
        onFine(dati as FineGenerazione);
      },
      errore: (messaggio) => {
        if (controller.signal.aborted) return;
        fallito = true;
        if (giaInCorso || (persa && onSulServer)) return;
        setErrore(messaggio);
        setFase('errore');
      },
    }, controller.signal);
    if (controllerRef.current === controller) controllerRef.current = null;
    onInCorso?.(false);
    // Interrotto: leggiSse non chiama nessun gestore. Il server, se lo stream era partito, continua
    // e salva alla fine: con `onSulServer` la pagina aspetta e lo si dice; senza, il foglio resta com'era.
    const interrotta = controller.signal.aborted && !finito && !fallito;
    if (giaInCorso) {
      // Un'altra generazione o impaginazione è in corso: non è un errore, il verbale arriverà da quella.
      setErrore(null);
      setFase('gia_in_corso');
      onSulServer?.();
      return;
    }
    if (onSulServer && ((interrotta && aperto) || persa)) {
      setErrore(null);
      setFase('sul_server');
      onSulServer();
      return;
    }
    if (interrotta) setFase('interrotta');
  };

  const inizia = () => {
    if (fase === 'in_corso' || disabilitato) return;
    if (haContenuto) setConferma(true);
    else avvia();
  };

  const inCorso = fase === 'in_corso';

  return (
    <div className="space-y-2">
      {pubblicato && !inCorso && (
        <p role="note" className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/25 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span>{AVVISO_GENERAZIONE_PUBBLICATO}</span>
        </p>
      )}
      {inCorso ? (
        <button
          type="button"
          onClick={() => controllerRef.current?.abort()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <Square className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
          Interrompi
        </button>
      ) : (
        <button
          type="button"
          onClick={inizia}
          disabled={disabilitato}
          className="inline-flex w-full disabled:cursor-not-allowed disabled:opacity-50 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-emerald-900/20 transition-all hover:from-emerald-700 hover:to-teal-700"
        >
          {fase === 'interrotta' || fase === 'errore'
            ? <RotateCcw className="h-4 w-4" aria-hidden="true" />
            : <Sparkles className="h-4 w-4" aria-hidden="true" />}
          Genera verbale dalla trascrizione
        </button>
      )}

      {(inCorso || (sezioni.length > 0 && fase !== 'fatta' && fase !== 'sul_server' && fase !== 'gia_in_corso')) && (
        <div
          ref={anteprimaRef}
          aria-label="Anteprima del verbale"
          aria-busy={inCorso}
          className="max-h-80 space-y-3 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/30"
        >
          {inCorso && sezioni.length === 0 && (
            <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Lettura della trascrizione…
            </p>
          )}
          {sezioni.map((s) => (
            <div key={s.chiave}>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-white">
                {s.html !== null
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                  : inCorso
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                    : null}
                {s.titolo}
              </p>
              {s.html !== null ? (
                <div
                  className="prose prose-sm mt-1 max-w-none text-sm text-slate-700 dark:prose-invert dark:text-gray-300"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.html) }}
                />
              ) : s.testo ? (
                <p className="mt-1 text-sm text-slate-600 dark:text-gray-300">{testoInArrivo(s.testo)}</p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {fase === 'interrotta' && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-900/20 dark:text-amber-200" role="status">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {TESTO_GENERA_INTERROTTO}: il verbale non è cambiato.
        </p>
      )}
      {(fase === 'sul_server' || fase === 'gia_in_corso') && (
        <p className="flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs font-medium text-sky-900 dark:bg-sky-900/20 dark:text-sky-200" role="status">
          <Loader2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 animate-spin" aria-hidden="true" />
          {fase === 'gia_in_corso' ? TESTO_GENERAZIONE_GIA_IN_CORSO : TESTO_GENERAZIONE_SUL_SERVER}
        </p>
      )}
      {fase === 'errore' && errore && (
        <p className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300" role="alert">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span>{errore.replace(/[.\s]+$/, '')}. {TESTO_GENERA_INTERROTTO}: il verbale non è cambiato.</span>
        </p>
      )}

      <ConfirmDialog
        isOpen={conferma}
        onClose={() => setConferma(false)}
        onConfirm={() => { avvia(); }}
        title="Generare il verbale dalla trascrizione?"
        message={TESTO_GENERA_SOSTITUISCE}
        confirmText="Genera"
        type="warning"
      />
    </div>
  );
};

export default VerbaleGenera;
